import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tables } from "@/integrations/supabase/types";
import { formatDistanceToNow } from "date-fns";
import { Car, Search, Phone, Mail, Archive, User, Calendar, Settings, Image as ImageIcon, CheckCircle2, Undo } from "lucide-react";
import { EmailComposeDialog } from "@/components/admin/EmailComposeDialog";
import { SmsComposeDialog } from "@/components/admin/SmsComposeDialog";
import { findOrCreateCustomer } from "@/utils/customerSync";

type ContactSubmission = Tables<"contact_submissions">;

export default function SellRequestsManagement() {
  const [leads, setLeads] = useState<ContactSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<ContactSubmission | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [adminNote, setAdminNote] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("id, name, email, phone, message, status, assigned_to, notes, customer_id, contact_preference, created_at")
      .ilike("message", "Sell Your Car Request:%")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load requests");
    } else {
      setLeads(data || []);
      
      if (selectedLead) {
        const updated = (data || []).find(l => l.id === selectedLead.id);
        if (updated) {
          setSelectedLead(updated);
          setAdminNote(getAdminNotes(updated.notes));
        } else {
          setSelectedLead(null);
        }
      }
    }
    setIsLoading(false);
  };

  const getAdminNotes = (notes: string | null): string => {
    if (!notes) return "";
    try {
      const parsed = JSON.parse(notes);
      return parsed._adminNotes || "";
    } catch {
      return notes || "";
    }
  };

  const getVehicleData = (notes: string | null) => {
    if (!notes) return null;
    try { return JSON.parse(notes); } catch { return null; }
  };

  const saveAdminNote = async () => {
    if (!selectedLead) return;
    const existingData = getVehicleData(selectedLead.notes) || {};
    const notesToSave = JSON.stringify({ ...existingData, _adminNotes: adminNote });

    const { error } = await supabase.from('contact_submissions').update({ notes: notesToSave }).eq('id', selectedLead.id);
    if (error) toast.error("Failed to save note");
    else {
      toast.success("Note saved");
      loadLeads();
    }
  };

  const updateLeadStatus = async (leadId: string, newStatus: string) => {
    const { error } = await supabase.from('contact_submissions').update({ status: newStatus }).eq('id', leadId);
    if (error) toast.error("Failed to update status");
    else {
      toast.success("Status updated");
      loadLeads();
    }
  };

  const filteredLeads = leads.filter(l => {
    const matchesSearch = l.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          l.email.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (l.phone && l.phone.includes(searchQuery));
    const matchesStatus = statusFilter === "all" ? l.status !== "archived" : l.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: "bg-blue-500 hover:bg-blue-600",
      contacted: "bg-yellow-500 hover:bg-yellow-600 text-yellow-950",
      processed: "bg-purple-500 hover:bg-purple-600",
      converted: "bg-green-500 hover:bg-green-600",
      archived: "bg-gray-500 hover:bg-gray-600"
    };
    return <Badge className={`${colors[status] || "bg-gray-500"} cursor-pointer transition-colors shadow-none font-semibold`} onClick={(e) => {
      e.stopPropagation();
      const next: Record<string, string> = { new: "contacted", contacted: "processed", processed: "new" };
      if (next[status] && selectedLead) updateLeadStatus(selectedLead.id, next[status]);
    }}>{status.toUpperCase()}</Badge>;
  };

  return (
    <div className="space-y-6 h-[calc(100vh-6rem)] flex flex-col">
      <div className="flex justify-between items-center shrink-0">
        <div>
          <h1 className="text-3xl font-heading font-bold">Sell Requests</h1>
          <p className="text-muted-foreground">Manage incoming vehicle sell offers</p>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 shrink-0">
        <Card><CardContent className="p-4 flex justify-between items-center"><div><p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Total Requests</p><p className="text-2xl font-bold">{leads.length}</p></div><Car className="h-8 w-8 text-muted/20" /></CardContent></Card>
        <Card><CardContent className="p-4 flex justify-between items-center"><div><p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">New Today</p><p className="text-2xl font-bold text-blue-600">{leads.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length}</p></div><Calendar className="h-8 w-8 text-blue-600/20" /></CardContent></Card>
        <Card><CardContent className="p-4 flex justify-between items-center"><div><p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Processed</p><p className="text-2xl font-bold text-purple-600">{leads.filter(l => l.status === 'processed').length}</p></div><CheckCircle2 className="h-8 w-8 text-purple-600/20" /></CardContent></Card>
        <Card><CardContent className="p-4 flex justify-between items-center"><div><p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Archived</p><p className="text-2xl font-bold text-gray-600">{leads.filter(l => l.status === 'archived').length}</p></div><Archive className="h-8 w-8 text-gray-600/20" /></CardContent></Card>
      </div>

      <div className="flex flex-1 gap-6 overflow-hidden min-h-0">
        {/* Left Panel - List */}
        <div className={`w-full ${selectedLead ? 'hidden md:flex' : 'flex'} md:w-1/3 flex-col gap-4 overflow-hidden border rounded-xl bg-card shadow-sm`}>
          <div className="p-4 border-b space-y-3 bg-muted/30 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search name, email or phone..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (Active)</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="processed">Processed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div> : 
             filteredLeads.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No requests match filters.</div> :
             filteredLeads.map(lead => {
               const vData = getVehicleData(lead.notes);
               const title = vData?.year ? `${vData.year} ${vData.make} ${vData.model}` : lead.name;
               const contactPref = lead.contact_preference || 'phone';
               
               return (
                 <div 
                   key={lead.id} 
                   onClick={() => { setSelectedLead(lead); setAdminNote(getAdminNotes(lead.notes)); }}
                   className={`p-4 border-b cursor-pointer transition-colors hover:bg-muted/50 flex flex-col gap-1 ${selectedLead?.id === lead.id ? 'bg-primary/5 border-l-4 border-l-primary' : 'border-l-4 border-l-transparent'}`}
                 >
                   <div className="flex justify-between items-start">
                     <span className="font-bold text-sm truncate pr-2 flex items-center gap-1">
                       <Car className="h-3.5 w-3.5 text-muted-foreground" /> {title}
                     </span>
                     {getStatusBadge(lead.status)}
                   </div>
                   <div className="text-xs text-muted-foreground truncate">{lead.name} • {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}</div>
                   <div className="flex justify-between items-center text-[10px] mt-1">
                     <Badge variant="outline" className="font-normal bg-slate-50 text-slate-500 uppercase flex items-center gap-1">
                       {contactPref === 'email' ? <Mail className="h-3 w-3" /> : <Phone className="h-3 w-3" />} PREFERS {contactPref}
                     </Badge>
                     {vData?.images && vData.images.length > 0 && (
                       <span className="flex items-center text-muted-foreground gap-1"><ImageIcon className="h-3 w-3"/> {vData.images.length}</span>
                     )}
                   </div>
                 </div>
               );
             })
            }
          </div>
        </div>

        {/* Right Panel - Detail */}
        {selectedLead ? (() => {
          const vData = getVehicleData(selectedLead.notes);
          const contactPref = selectedLead.contact_preference || 'phone';
          
          return (
            <div className="w-full md:w-2/3 flex flex-col bg-card border rounded-xl shadow-sm overflow-hidden min-h-0">
              {/* Detail Header */}
              <div className="p-6 border-b shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-muted/10">
                <div className="flex items-center gap-4">
                  <Button variant="ghost" size="sm" className="md:hidden -ml-2" onClick={() => setSelectedLead(null)}>← Back</Button>
                  <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xl font-bold">
                    <Car className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{vData?.year ? `${vData.year} ${vData.make} ${vData.model}` : 'Vehicle Details'}</h2>
                    <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                      <span>Submitted by <strong>{selectedLead.name}</strong></span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(true)}>
                      <Mail className="h-4 w-4 mr-1"/> Email
                    </Button>
                    {selectedLead.phone && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={async () => {
                          if (!selectedLead.customer_id) {
                            const nameParts = selectedLead.name.trim().split(' ');
                            const firstName = nameParts[0];
                            const lastName = nameParts.slice(1).join(' ') || '-';
                            const customerId = await findOrCreateCustomer({ firstName, lastName, email: selectedLead.email, phone: selectedLead.phone || undefined });
                            if (customerId) {
                              await supabase.from('contact_submissions').update({ customer_id: customerId }).eq('id', selectedLead.id);
                              selectedLead.customer_id = customerId;
                            }
                          }
                          setSmsDialogOpen(true);
                        }}
                      >
                        <Phone className="h-4 w-4 mr-1"/> SMS
                      </Button>
                    )}
                    {selectedLead.status !== 'archived' ? (
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => updateLeadStatus(selectedLead.id, 'archived')}>
                        <Archive className="h-4 w-4 mr-1"/> Archive
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => updateLeadStatus(selectedLead.id, 'new')}>
                        <Undo className="h-4 w-4 mr-1"/> Restore
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Detail Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-blue-50/50 border border-blue-100 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1">Prefers Contact By</p>
                    <p className="font-medium text-blue-900 flex items-center gap-1">
                      {contactPref === 'email' ? <Mail className="h-4 w-4" /> : <Phone className="h-4 w-4" />} {contactPref.toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1">Email</p>
                    <p className="font-medium">{selectedLead.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1">Phone</p>
                    <p className="font-medium">{selectedLead.phone || 'N/A'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Vehicle Specs */}
                  <div className="bg-muted/30 p-4 rounded-lg border space-y-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2"><Settings className="h-4 w-4 text-primary" /> Vehicle Specs</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">VIN:</span> <span className="font-mono">{vData?.vin || 'N/A'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Odometer:</span> <span className="font-medium">{vData?.odometer ? `${vData.odometer.toLocaleString()} KM` : 'N/A'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Trim:</span> <span className="font-medium">{vData?.trim || 'N/A'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Transmission:</span> <span className="font-medium">{vData?.transmission || 'N/A'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Color (Ext/Int):</span> <span className="font-medium">{vData?.exteriorColor} / {vData?.interiorColor}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Location:</span> <span className="font-medium">{vData?.city}, {vData?.province}</span></div>
                    </div>
                  </div>

                  {/* Condition Report */}
                  <div className="bg-muted/30 p-4 rounded-lg border space-y-4">
                    <h3 className="font-semibold text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Condition Report</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Accident Claims:</span> <span className="font-medium">{String(vData?.accidentClaims) === 'true' ? 'Yes' : 'No'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Smoked In:</span> <span className="font-medium">{String(vData?.smokedIn) === 'true' ? 'Yes' : 'No'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Exterior Damage:</span> <span className="font-medium">{String(vData?.exteriorDamage) === 'true' ? 'Yes' : 'No'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Interior Damage:</span> <span className="font-medium">{String(vData?.interiorDamage) === 'true' ? 'Yes' : 'No'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Windshield Cracked:</span> <span className="font-medium">{String(vData?.windshieldCrack) === 'true' ? 'Yes' : 'No'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Sets of Keys:</span> <span className="font-medium">{vData?.keys || '1'}</span></div>
                    </div>
                  </div>
                </div>

                {vData?.reasonForSelling && (
                  <div>
                    <h3 className="text-sm font-semibold uppercase text-muted-foreground tracking-wider mb-2">Reason for Selling</h3>
                    <div className="p-3 bg-muted/30 rounded-lg text-sm border border-dashed">
                      {vData.reasonForSelling}
                    </div>
                  </div>
                )}

                {/* Photos */}
                {vData?.images && vData.images.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold uppercase text-muted-foreground tracking-wider mb-2 flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" /> Photos ({vData.images.length})
                    </h3>
                    <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-thin">
                      {vData.images.map((url: string, idx: number) => (
                        <div 
                          key={idx} 
                          className="relative flex-none w-32 h-32 rounded-lg overflow-hidden cursor-pointer border shadow-sm hover:border-primary hover:shadow-md transition-all" 
                          onClick={() => setSelectedImage(url)}
                        >
                          <img src={url} alt={`Vehicle Photo ${idx+1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin Notes */}
                <div>
                  <h3 className="text-sm font-semibold uppercase text-muted-foreground tracking-wider mb-2 flex justify-between">
                    <span>Admin Notes</span>
                    <Button variant="ghost" size="sm" className="h-5 text-xs text-primary" onClick={saveAdminNote}>Save Notes</Button>
                  </h3>
                  <Textarea 
                    value={adminNote} 
                    onChange={e => setAdminNote(e.target.value)} 
                    placeholder="Private notes regarding this vehicle evaluation..."
                    maxLength={2000}
                    className="min-h-[100px] bg-yellow-50/50 border-yellow-200 focus-visible:ring-yellow-400"
                  />
                </div>
              </div>
            </div>
          );
        })() : (
          <div className="hidden md:flex w-2/3 flex-col items-center justify-center bg-muted/10 border rounded-xl text-muted-foreground">
            <Car className="h-16 w-16 mb-4 text-muted-foreground/30" />
            <p className="text-lg font-medium">Select a request</p>
            <p className="text-sm">Choose a sell request to evaluate vehicle details</p>
          </div>
        )}
      </div>

      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black/95 border-none">
          {selectedImage && <img src={selectedImage} alt="Vehicle Preview" className="w-full h-auto max-h-[90vh] object-contain" />}
        </DialogContent>
      </Dialog>

      {/* Communication Dialogs */}
      {selectedLead && (
        <>
          <EmailComposeDialog
            open={emailDialogOpen}
            onOpenChange={setEmailDialogOpen}
            recipientEmail={selectedLead.email}
            recipientName={selectedLead.name}
            customerId={selectedLead.customer_id}
            defaultSubject={(() => {
              const vData = getVehicleData(selectedLead.notes);
              return vData?.year ? `Regarding your ${vData.year} ${vData.make} ${vData.model}` : "Regarding your vehicle evaluation";
            })()}
          />
          {selectedLead.phone && selectedLead.customer_id && (
            <SmsComposeDialog
              open={smsDialogOpen}
              onOpenChange={setSmsDialogOpen}
              customerId={selectedLead.customer_id}
              customerName={selectedLead.name}
              customerPhone={selectedLead.phone}
            />
          )}
        </>
      )}
    </div>
  );
}
